import React from 'react';
import { FiAlertTriangle, FiCheck, FiX } from 'react-icons/fi';

const ConfirmationPopup = ({
    isOpen,
    onClose,
    onConfirm,
    title = "Confirm Action",
    message = "Are you sure you want to proceed?",
    confirmText = "Confirm",
    cancelText = "Cancel",
    type = "default", // "default", "danger", "warning", "success"
    showIcon = true,
    className = "",
    showInput = false,
    inputPlaceholder = "Enter notes...",
    inputValue = "",
    setInputValue = () => { },
    showToggle = false,
    toggleTitle = "Enable option?",
    toggleValue = false,
    setToggleValue = () => { },
}) => {
    if (!isOpen) return null;

    const getTypeStyles = () => {
        switch (type) {
            case "danger":
                return {
                    icon: <FiAlertTriangle className="w-6 h-6 text-red-500" />,
                    confirmButton: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
                    cancelButton: "border-gray-300 text-gray-700 hover:bg-gray-50",
                    iconBg: "bg-red-100"
                };
            case "warning":
                return {
                    icon: <FiAlertTriangle className="w-6 h-6 text-yellow-500" />,
                    confirmButton: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
                    cancelButton: "border-gray-300 text-gray-700 hover:bg-gray-50",
                    iconBg: "bg-yellow-100"
                };
            case "success":
                return {
                    icon: <FiCheck className="w-6 h-6 text-green-500" />,
                    confirmButton: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
                    cancelButton: "border-gray-300 text-gray-700 hover:bg-gray-50",
                    iconBg: "bg-green-100"
                };
            default:
                return {
                    icon: <FiAlertTriangle className="w-6 h-6 text-blue-500" />,
                    confirmButton: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
                    cancelButton: "border-gray-300 text-gray-700 hover:bg-gray-50",
                    iconBg: "bg-blue-100"
                };
        }
    };

    const styles = getTypeStyles();

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
                onClick={handleBackdropClick}
            >
                {/* Popup Container */}
                <div className={`bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-200 ${className}`}>
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div className="flex items-center gap-3">
                            {showIcon && (
                                <div className={`p-2 rounded-full ${styles.iconBg}`}>
                                    {styles.icon}
                                </div>
                            )}
                            <h3 className="text-lg font-semibold text-gray-900">
                                {title}
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 rounded-full hover:bg-gray-100"
                            aria-label="Close"
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <p className="text-gray-600 leading-relaxed mb-4">
                            {message}
                        </p>
                        {showToggle && (
                            <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg mb-4">
                                <span className="text-sm font-medium text-gray-700">{toggleTitle}</span>
                                <div
                                    onClick={() => setToggleValue(!toggleValue)}
                                    className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors cursor-pointer ${toggleValue ? 'bg-primary' : 'bg-gray-200'}`}
                                >
                                    <span
                                        className={`inline-block w-4 h-4 transform transition-transform rounded-full bg-white shadow-md ${toggleValue ? 'translate-x-6' : 'translate-x-1'}`}
                                    />
                                </div>
                            </div>
                        )}
                        {showInput && (
                            <textarea
                                className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 resize-none"
                                placeholder={inputPlaceholder}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                autoFocus
                            />
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${styles.cancelButton}`}
                        >
                            {cancelText}
                        </button>
                        <button
                            onClick={handleConfirm}
                            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.confirmButton}`}
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ConfirmationPopup;
